import type { CSSProperties } from 'react';

/**
 * Shared placeholder primitives used by every page's skeleton card/row.
 *
 * These are NOT animated shimmers. They are static gray bars that take the
 * place of real text/badges/avatars so each page's card layout reads
 * correctly before backend data is wired. Opacity is reduced at the
 * container level (the parent card uses `bdse-skeleton-card` modifier) so
 * the eye registers these as preview rather than data.
 *
 * If we later want shimmer animation, add a keyframe in global.css and the
 * primitives will pick it up via a new class.
 */

export function SkelBar({
  w,
  h = 9,
  style,
}: {
  /** Width as a CSS length or percentage, e.g. "60%" or 120 */
  w: number | string;
  /** Height in px. Default 9. */
  h?: number;
  style?: CSSProperties;
}) {
  const width = typeof w === 'number' ? `${w}px` : w;
  return (
    <span
      className="bdse-skel-bar"
      style={{ width, height: h, ...style }}
      aria-hidden="true"
    />
  );
}

export function SkelBadge({
  w = 64,
  h = 16,
  style,
}: {
  w?: number | string;
  h?: number;
  style?: CSSProperties;
}) {
  const width = typeof w === 'number' ? `${w}px` : w;
  return (
    <span
      className="bdse-skel-badge"
      style={{ width, height: h, ...style }}
      aria-hidden="true"
    />
  );
}

export function SkelAvatar({ size = 36 }: { size?: number }) {
  return (
    <span
      className="bdse-skel-avatar"
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

export function SkelCircle({ size = 36 }: { size?: number }) {
  return (
    <span
      className="bdse-skel-circle"
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

/**
 * Section heading shown above a skeleton group, e.g. "Sample preview".
 * Sets context that the rows below are placeholders, not data.
 */
export function SkeletonPreviewLabel({ text }: { text: string }) {
  return <div className="bdse-skel-preview-label">{text}</div>;
}
